<template>
    <div class="eam-card">
        <!-- Header -->
        <div class="eam-header">
            <div class="eam-header-left">
                <div class="eam-header-icon"><i class="fas fa-box-open"></i></div>
                <div>
                    <h3 class="eam-header-title">Asset Management</h3>
                    <p class="eam-header-subtitle">Track allocated assets and returns</p>
                </div>
            </div>
            <button class="eam-btn-allocate" @click="$router.push('/asset-allocations')">
                <i class="fas fa-plus-circle"></i> Allocate Asset
            </button>
        </div>

        <!-- Stats Bar (when assets exist) -->
        <div v-if="!loading && assetAllocations.length" class="eam-stats">
            <div class="eam-stat">
                <div class="eam-stat-num">{{ assetAllocations.length }}</div>
                <div class="eam-stat-label">Total Assets</div>
            </div>
            <div class="eam-stat">
                <div class="eam-stat-num">{{ allocatedCount }}</div>
                <div class="eam-stat-label">Allocated</div>
            </div>
            <div class="eam-stat">
                <div class="eam-stat-num">{{ returnedCount }}</div>
                <div class="eam-stat-label">Returned</div>
            </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="eam-body">
            <div class="eam-loading">
                <i class="fas fa-circle-notch fa-spin"></i>
                <span>Loading assets...</span>
            </div>
        </div>

        <!-- Table -->
        <div v-else-if="assetAllocations.length" class="eam-table-wrap">
            <table class="eam-table">
                <thead>
                    <tr>
                        <th class="th-asset">
                            <i class="fas fa-cube"></i> Asset Name
                        </th>
                        <th class="th-date">
                            <i class="fas fa-calendar-alt"></i> Allocated Date
                        </th>
                        <th class="th-status">
                            <i class="fas fa-circle-notch"></i> Status
                        </th>
                        <th class="th-date">
                            <i class="fas fa-undo-alt"></i> Returned Date
                        </th>
                        <th class="th-actions">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <EmployeeAssetManagerRow
                        :assetAllocation="assetAllocation"
                        v-for="(assetAllocation, i) of assetAllocations"
                        :key="assetAllocation.id"
                        @openReturnModal="openReturnModal"
                    />
                </tbody>
            </table>
        </div>

        <!-- Empty State -->
        <div v-else class="eam-body">
            <div class="eam-empty">
                <div class="eam-empty-icon">
                    <i class="fas fa-box-open"></i>
                </div>
                <h4 class="eam-empty-title">No Assets Allocated</h4>
                <p class="eam-empty-desc">This employee hasn't been assigned any assets yet.</p>
                <button class="eam-btn-primary-lg" @click="$router.push('/asset-allocations')">
                    <i class="fas fa-plus"></i> Allocate Your First Asset
                </button>
            </div>
        </div>

        <!-- Return Modal -->
        <transition name="aa-modal-fade">
            <div class="aa-modal-overlay" v-if="returnModal.show" @click.self="closeReturnModal">
                <div class="aa-modal">
                    <div class="aa-modal-header">
                        <div class="aa-modal-icon"><i class="fas fa-undo-alt"></i></div>
                        <div>
                            <h3 class="aa-modal-title">Return Asset</h3>
                            <p class="aa-modal-sub">{{ returnModal.assetName }}</p>
                        </div>
                        <button class="aa-modal-close" @click="closeReturnModal"><i class="fas fa-times"></i></button>
                    </div>

                    <div class="aa-modal-body">
                        <div class="aa-modal-field">
                            <label class="aa-modal-label">
                                <i class="fas fa-tag"></i> Return Reason <span class="aa-req">*</span>
                            </label>
                            <div class="aa-reason-grid">
                                <button
                                    v-for="r in returnReasons"
                                    :key="r.id"
                                    class="aa-reason-btn"
                                    :class="{ 'aa-reason-active': returnModal.reasonId === r.id }"
                                    @click="returnModal.reasonId = r.id"
                                >
                                    <i :class="r.icon"></i>
                                    <span>{{ r.label }}</span>
                                </button>
                            </div>
                        </div>

                        <div class="aa-modal-field">
                            <label class="aa-modal-label">
                                <i class="fas fa-comment-alt"></i> Additional Notes
                            </label>
                            <textarea
                                class="aa-modal-textarea"
                                v-model="returnModal.notes"
                                placeholder="Describe the condition of the asset, any damage, or other relevant details..."
                                rows="3"
                            ></textarea>
                        </div>
                    </div>

                    <div class="aa-modal-footer">
                        <button class="aa-modal-cancel" @click="closeReturnModal">Cancel</button>
                        <button
                            class="aa-modal-confirm"
                            :disabled="!returnModal.reasonId || returning"
                            @click="confirmReturn"
                        >
                            <i class="fas fa-undo-alt"></i>
                            {{ returning ? 'Processing...' : 'Confirm Return' }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { errMsg, formatIndianDate } from "../../utils/common"
import { http } from "../../utils/http"
import EmployeeAssetManagerRow from "./EmployeeAssetManagerRow.vue"

export default {
    data() {
        return {
            assetAllocations: [],
            loading: true,
            returning: false,
            returnModal: {
                show: false,
                allocationId: null,
                assetName: "",
                reasonId: null,
                notes: "",
            },
            returnReasons: [
                { id: "not_working",         label: "Not Working / Malfunctioning", icon: "fas fa-exclamation-triangle" },
                { id: "damaged",             label: "Damaged",                      icon: "fas fa-tools" },
                { id: "scrapped",            label: "Asset Scrapped",               icon: "fas fa-trash-alt" },
                { id: "employee_terminated", label: "Employee Terminated",          icon: "fas fa-user-times" },
                { id: "employee_resigned",   label: "Employee Resigned / Job Left", icon: "fas fa-sign-out-alt" },
                { id: "upgraded",            label: "Upgraded / Replaced",          icon: "fas fa-sync-alt" },
                { id: "project_completed",   label: "Project Completed",            icon: "fas fa-check-circle" },
                { id: "other",               label: "Other",                        icon: "fas fa-ellipsis-h" },
            ],
        }
    },
    components: { EmployeeAssetManagerRow },
    props: {
        employeeId: {
            type: [String, Number],
            default: null,
        },
    },
    computed: {
        allocatedCount() {
            return this.assetAllocations.filter((a) => !a.returned).length
        },
        returnedCount() {
            return this.assetAllocations.filter((a) => a.returned).length
        },
    },
    mounted() {
        this.fetchAssets()
    },
    methods: {
        formatIndianDate,
        openReturnModal(allocation) {
            this.returnModal = {
                show: true,
                allocationId: allocation.id,
                assetName: allocation.asset?.name || "Asset",
                reasonId: null,
                notes: "",
            }
        },
        closeReturnModal() {
            this.returnModal.show = false
        },
        async confirmReturn() {
            if (!this.returnModal.reasonId || this.returning) return
            this.returning = true
            const reason = this.returnReasons.find((r) => r.id === this.returnModal.reasonId)
            try {
                const res = await http.patch(`/asset-allocations/${this.returnModal.allocationId}/return`, {
                    returnReasonId:    this.returnModal.reasonId,
                    returnReasonLabel: reason ? reason.label : "",
                    returnNotes:       this.returnModal.notes,
                })
                const returned = res.data.result
                this.assetAllocations = this.assetAllocations.map((item) =>
                    item.id === this.returnModal.allocationId
                        ? { ...item, returned: true, returnedDate: returned?.returnedDate }
                        : item
                )
                this.$toast.success("Asset returned successfully!")
                this.closeReturnModal()
            } catch (err) {
                console.error("Error returning asset:", err)
                this.$toast.error(errMsg(err))
            }
            this.returning = false
        },
        async fetchAssets() {
            this.loading = true
            try {
                const res = await http.get(`/asset-allocations?employee_id=${this.employeeId}`)
                this.assetAllocations = res.data.result
            } catch (err) {
                console.error("Error loading assets:", err)
            }
            this.loading = false
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.eam-card {
    background: white;
    border: 1px solid $gray100;
    border-radius: 10px;
    box-shadow: $shadowSm;
    overflow: hidden;
    margin-bottom: 20px;
}

/* ── Header ── */
.eam-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 20px;
    border-bottom: 1px solid $gray100;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    flex-wrap: wrap;
}
.eam-header-left {
    display: flex;
    align-items: center;
    gap: 12px;
}
.eam-header-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
    color: #1d4ed8;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
}
.eam-header-title {
    font-size: 15px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
}
.eam-header-subtitle {
    font-size: 12px;
    color: $gray500;
    margin: 2px 0 0;
}

/* ── Allocate Button ── */
.eam-btn-allocate {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: white;
    color: #1d4ed8;
    border: 1.5px solid #bfdbfe;
    border-radius: 7px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.15s;
    white-space: nowrap;

    i { font-size: 13px; }

    &:hover {
        background: #eff6ff;
        border-color: #3b82f6;
        color: #1e40af;
    }
}

/* ── Stats Bar ── */
.eam-stats {
    display: flex;
    gap: 1px;
    background: $gray100;
    border-bottom: 1px solid $gray100;
}
.eam-stat {
    flex: 1;
    padding: 12px 16px;
    background: white;
    text-align: center;
    border-right: 1px solid $gray100;

    &:last-child { border-right: none; }
}
.eam-stat-num {
    font-size: 20px;
    font-weight: 700;
    color: #1d4ed8;
    line-height: 1;
    margin-bottom: 3px;
}
.eam-stat-label {
    font-size: 11px;
    font-weight: 600;
    color: $gray500;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

/* ── Body / Content ── */
.eam-body { padding: 20px; }

/* ── Loading ── */
.eam-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 40px 20px;
    color: $gray400;
    font-size: 13px;

    i { color: #1d4ed8; font-size: 16px; animation: spin 1s linear infinite; }
}

@keyframes spin { to { transform: rotate(360deg); } }

/* ── Table ── */
.eam-table-wrap { overflow-x: auto; }
.eam-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    thead {
        background: $gray50;
        border-bottom: 2px solid $gray200;
    }
    th {
        padding: 12px 28px;
        text-align: left;
        font-weight: 600;
        font-size: 11px;
        color: $gray600;
        text-transform: uppercase;
        letter-spacing: 0.4px;
        white-space: nowrap;

        i { margin-right: 5px; font-size: 10px; color: $gray400; }
    }
    .th-asset { min-width: 160px; }
    .th-date { min-width: 120px; }
    .th-status { min-width: 110px; }
    .th-actions { width: 90px; text-align: center; }

    tbody tr {
        border-bottom: 1px solid $gray100;
        transition: all 0.15s;

        &:hover {
            background: #f9fafc;
        }
        &:last-child { border-bottom: none; }
    }
    td {
        padding: 12px 28px;
        color: $gray700;
        vertical-align: middle;
    }
}

/* ── Empty State ── */
.eam-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 48px 20px;
}
.eam-empty-icon {
    width: 64px;
    height: 64px;
    border-radius: 12px;
    background: #f1f5f9;
    color: $gray300;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    margin-bottom: 16px;
}
.eam-empty-title {
    font-size: 15px;
    font-weight: 700;
    color: $gray700;
    margin: 0 0 6px;
}
.eam-empty-desc {
    font-size: 13px;
    color: $gray500;
    margin: 0 0 18px;
    max-width: 360px;
}

/* ── Primary Button ── */
.eam-btn-primary-lg {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 10px 20px;
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border: none;
    border-radius: 7px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.15s;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    }
}

/* ── Modal Styles (from asset-allocations) ── */
.aa-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    backdrop-filter: blur(3px);
    z-index: 3000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
}
.aa-modal {
    background: white;
    border-radius: 14px;
    width: 100%;
    max-width: 520px;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
    overflow: hidden;
}
.aa-modal-header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 18px 20px 14px;
    border-bottom: 1px solid $gray100;
}
.aa-modal-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: #faf5ff;
    color: #7c3aed;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    flex-shrink: 0;
}
.aa-modal-title {
    font-size: 15px;
    font-weight: 700;
    color: $gray800;
    margin: 0 0 2px;
}
.aa-modal-sub {
    font-size: 12px;
    color: $gray400;
    margin: 0;
    font-weight: 500;
}
.aa-modal-close {
    margin-left: auto;
    width: 28px;
    height: 28px;
    border: 1px solid $gray200;
    border-radius: 6px;
    background: white;
    color: $gray400;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    flex-shrink: 0;
    transition: all $transitionFast;

    &:hover {
        background: $gray50;
        color: $gray600;
    }
}
.aa-modal-body {
    padding: 18px 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.aa-modal-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.aa-modal-label {
    font-size: 12px;
    font-weight: 600;
    color: $gray600;
    display: flex;
    align-items: center;
    gap: 6px;

    i { font-size: 11px; color: $gray400; }
}
.aa-req { color: #dc2626; }

.aa-reason-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 7px;
}
.aa-reason-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 12px;
    border: 1.5px solid $gray200;
    border-radius: 8px;
    background: white;
    font-size: 12px;
    font-weight: 500;
    color: $gray600;
    cursor: pointer;
    font-family: inherit;
    transition: all $transitionFast;
    text-align: left;

    i { font-size: 12px; color: $gray400; flex-shrink: 0; transition: color $transitionFast; }

    &:hover {
        border-color: $gray300;
        background: $gray50;
        color: $gray800;
    }
    &.aa-reason-active {
        border-color: #7c3aed;
        background: #faf5ff;
        color: #7c3aed;
        font-weight: 600;

        i { color: #7c3aed; }
    }
}

.aa-modal-textarea {
    width: 100%;
    border: 1.5px solid $gray200;
    border-radius: 8px;
    padding: 9px 12px;
    font-size: 12.5px;
    font-family: inherit;
    color: $gray700;
    resize: vertical;
    outline: none;
    transition: border-color 0.15s;
    line-height: 1.5;
    box-sizing: border-box;

    &::placeholder { color: $gray400; }
    &:focus { border-color: #7c3aed; box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.08); }
}

.aa-modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 20px;
    border-top: 1px solid $gray100;
    background: $gray50;
}
.aa-modal-cancel {
    padding: 7px 16px;
    border: 1px solid $gray200;
    border-radius: 6px;
    background: white;
    color: $gray600;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: all $transitionFast;

    &:hover { background: $gray100; }
}
.aa-modal-confirm {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 18px;
    border: none;
    border-radius: 6px;
    background: #7c3aed;
    color: white;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: all $transitionFast;

    i { font-size: 11px; }

    &:hover:not(:disabled) { background: #6d28d9; }
    &:disabled { opacity: 0.45; cursor: not-allowed; }
}

.aa-modal-fade-enter-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.aa-modal-fade-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.aa-modal-fade-enter, .aa-modal-fade-leave-to { opacity: 0; }
.aa-modal-fade-enter .aa-modal, .aa-modal-fade-leave-to .aa-modal { transform: scale(0.96) translateY(6px); }

@media (max-width: 768px) {
    .eam-header { flex-direction: column; align-items: stretch; }
    .eam-btn-allocate { justify-content: center; }
    .eam-stats { flex-direction: column; }
    .eam-stat { border-right: none; border-bottom: 1px solid $gray100; }
    .eam-stat:last-child { border-bottom: none; }
}
</style>
